import { useCallback, useEffect, useState } from 'react';
import { Building2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { tax } from '../lib/api';
import { fmtStamp } from './LiveClock';

const FIELDS = [
  { k: 'legal_name', label: 'Registered company name', ph: 'ADVN SOLUTIONS LLP', span: 2 },
  { k: 'trade_name', label: 'Brand / trade name', ph: 'BharatCart' },
  { k: 'cin', label: 'CIN (optional)', ph: 'U12345TG2026PTC123456', upper: true },
  { k: 'address', label: 'Registered address', ph: 'Plot no, street, city, PIN', span: 2, area: true },
  { k: 'state_name', label: 'State', ph: 'Telangana' },
  { k: 'state_code', label: 'State code', ph: '36', upper: true },
  { k: 'pan', label: 'Company PAN', ph: 'AABCM1234N', upper: true },
  { k: 'tan', label: 'TAN (for TDS)', ph: 'HYDM12345A', upper: true },
  { k: 'gstin', label: 'GSTIN (when received)', ph: '36AABCM1234N1Z5', upper: true },
  { k: 'email', label: 'Accounts email', ph: 'accounts@bharatcart.org' },
  { k: 'phone', label: 'Accounts phone', ph: '9xxxxxxxxx' },
];
const RATES = [
  { k: 'tcs_rate_pct', label: 'TCS % (GST Sec 52)' },
  { k: 'tds_rate_pct', label: 'TDS % (Income-tax 194-O)' },
  { k: 'tds_rate_no_pan_pct', label: 'TDS % if seller has no PAN' },
];

export default function CompanyProfileTab() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => tax.company().then(setForm).catch(() => toast.error('Could not load company profile')), []);
  useEffect(() => { load(); }, [load]);
  if (!form) return <div className="mt-6 text-sm text-ink/60">Loading…</div>;

  const set = (k, v, upper) => setForm((f) => ({ ...f, [k]: upper ? v.toUpperCase() : v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form };
      RATES.forEach(({ k }) => { body[k] = parseFloat(body[k]) || 0; });
      setForm(await tax.saveCompany(body));
      toast.success('Company details saved — they now print on invoices, POs and tax statements');
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="company-profile-tab" className="mt-6 card-soft p-5">
      <div className="flex items-center gap-2">
        <Building2 className="w-5 h-5 text-[#0D47A1]"/>
        <div className="font-heading font-bold text-lg">Company legal & tax details</div>
        <span data-testid="company-updated-at" className="ml-auto text-[11px] text-ink/60">Last saved {fmtStamp(form.updated_at)}</span>
      </div>
      <p className="text-xs text-ink/60 mt-1">Printed on every tax invoice, purchase order PDF and seller tax statement. Leave GSTIN blank until it arrives.</p>
      <div className="mt-4 grid md:grid-cols-2 gap-3">
        {FIELDS.map(({ k, label, ph, span, area, upper }) => (
          <label key={k} className={`text-xs font-bold text-ink/70 ${span === 2 ? 'md:col-span-2' : ''}`}>
            {label}
            {area ? (
              <textarea data-testid={`company-${k}`} value={form[k] || ''} onChange={(e) => set(k, e.target.value)} placeholder={ph} rows={2}
                className="mt-1 w-full border-2 border-ink/15 rounded-lg px-3 py-2 text-sm font-normal outline-none focus:border-ink"/>
            ) : (
              <input data-testid={`company-${k}`} value={form[k] || ''} onChange={(e) => set(k, e.target.value, upper)} placeholder={ph}
                className={`mt-1 w-full border-2 border-ink/15 rounded-lg px-3 h-10 text-sm font-normal outline-none focus:border-ink ${upper ? 'font-mono' : ''}`}/>
            )}
          </label>
        ))}
      </div>
      <div className="mt-5 text-[10px] uppercase tracking-[0.2em] font-black text-ink/60">Tax rates (confirm with your CA)</div>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {RATES.map(({ k, label }) => (
          <label key={k} className="text-xs font-bold text-ink/70">
            {label}
            <input data-testid={`company-${k}`} type="number" step="0.01" min="0" value={form[k]} onChange={(e) => set(k, e.target.value)}
              className="mt-1 w-full border-2 border-ink/15 rounded-lg px-3 h-10 text-sm font-normal outline-none focus:border-ink"/>
          </label>
        ))}
      </div>
      <button data-testid="company-save" onClick={save} disabled={busy} className="btn-primary mt-5 !py-2.5 !px-5 text-sm"><Save className="w-4 h-4"/>Save company details</button>
    </div>
  );
}
